// ============================================================
// Recipe Cost — route entry / integration wrapper
// ============================================================
// The approved El Campesino mockup (RecipeCostMockup.jsx) is a self-contained
// Tailwind + lucide-react app. BeeNexa's CRA build ships neither, so this
// wrapper:
//   1. Lazily injects the Tailwind Play CDN + the Inter / JetBrains Mono
//      fonts the mockup's design tokens expect (chosen approach: Play CDN —
//      demo-grade, zero build config).
//   2. Resolves the tenant `locationId` from the auth slice (same rule as
//      BusinessTracker: company || companyId) and hands it to the app so the
//      Master List / recipes / analytics can persist to the backend.
//
// Production note (spec § 7.2): swap the Play CDN for a bundled Tailwind
// build and bundle jsPDF/html2canvas so the module works offline in the
// kitchen. Tracked as a follow-up.

import React, { useEffect } from 'react';
import withShipment from '../../withShipment';
import { userInfoSelector } from '../../redux/selectors/auth';
import { getSpaceId } from '../../hive/getSpaceId';
import { RecipeCostApp } from './RecipeCostMockup';

// Load a <script>/<link> once, keyed by id so route re-mounts don't duplicate.
function ensureTag(tag, id, attrs) {
  if (document.getElementById(id)) return;
  const el = document.createElement(tag);
  el.id = id;
  Object.keys(attrs).forEach((k) => { el[k] = attrs[k]; });
  document.head.appendChild(el);
}

function useRecipeFonts() {
  useEffect(() => {
    // Tailwind itself is now bundled + scoped via ./recipeCost.tailwind.css
    // (imported in RecipeCostMockup) — no runtime CDN. We still opportunistically
    // load the Inter / JetBrains Mono webfonts the design tokens reference; if
    // these are blocked/offline the tokens fall back to system sans/mono cleanly.
    ensureTag('link', 'ec-fonts-preconnect', {
      rel: 'preconnect', href: 'https://fonts.googleapis.com',
    });
    ensureTag('link', 'ec-fonts', {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;700&display=swap',
    });
  }, []);
}

function RecipeCost({ userInfo }) {
  useRecipeFonts();
  // Data scope follows the active Hive Space (defaults to the user's home space).
  const locationId = getSpaceId(userInfo);
  return <RecipeCostApp locationId={locationId} />;
}

export default withShipment(
  {
    mapStateToProps: (state) => ({ userInfo: userInfoSelector(state) }),
    actionCreators: {},
  },
  RecipeCost,
);
